import { oids } from "@/configs/Settings";
import { useFhirData } from "@/utils/useFhirData";
import { fhirClients } from "src-next/shared/api/fhirClient";
import { normalizeCpf } from "src-next/shared/utils/cpf";
import type {
  BatchPersonFailure,
  BatchPersonSexInput,
  BatchPersonValidRow,
} from "./types";

const TERM_REFUSED_URL = "https://fluxmed.com.br/termRefused";

function prefixedOrganizationId(organizationId: string): string {
  return useFhirData.ensureOrganizationIdWithOidPrefix(organizationId);
}

export function buildPersonOrganizationReference(organizationId: string): string {
  return `Organization/${prefixedOrganizationId(organizationId)}`;
}

function extractPersons(bundle: fhir5.Bundle | undefined): fhir5.Person[] {
  return (bundle?.entry ?? [])
    .map((entry) => entry.resource)
    .filter((resource): resource is fhir5.Person => resource?.resourceType === "Person");
}

export function personBelongsToOrganization(
  person: fhir5.Person,
  organizationId: string,
): boolean {
  const prefixed = prefixedOrganizationId(organizationId);
  const reference = person.managingOrganization?.reference ?? "";
  return (
    reference === `Organization/${prefixed}` ||
    reference.endsWith(`/${prefixed}`) ||
    reference === prefixed
  );
}

export function extractPersonCpf(person: fhir5.Person): string {
  const bySystem = person.identifier?.find((item) =>
    item.system?.includes(oids.cpf),
  )?.value;
  const value = bySystem || person.identifier?.[0]?.value || "";
  return normalizeCpf(value);
}

/**
 * Lista Persons da organização.
 * 1) `_summary=count` — total. 2) `_count={total}` — busca única. Sem paginação.
 */
export async function fetchPersonsByOrganization(
  organizationId: string,
): Promise<fhir5.Person[]> {
  const reference = buildPersonOrganizationReference(organizationId);
  const baseParams = { organization: reference };

  const countBundle = await fhirClients.admin.search<fhir5.Person>("Person", {
    ...baseParams,
    _summary: "count",
  });
  const total = countBundle.total ?? 0;
  if (total === 0) return [];

  const bundle = await fhirClients.admin.search<fhir5.Person>("Person", {
    ...baseParams,
    _count: String(total),
  });

  return extractPersons(bundle).filter((person) =>
    personBelongsToOrganization(person, organizationId),
  );
}

export function collectOrganizationCpfs(persons: fhir5.Person[]): Set<string> {
  const cpfs = new Set<string>();
  persons.forEach((person) => {
    const cpf = extractPersonCpf(person);
    if (cpf) cpfs.add(cpf);
  });
  return cpfs;
}

/**
 * Converte o sexo do arquivo para o valor gravado no Person.
 */
function toPersonGender(sex: BatchPersonSexInput): "male" | "female" {
  if (sex === "m" || sex === "masculino") return "male";
  return "female";
}

/**
 * Cadastro em lote (CSV/JSON): cria Person com POST.
 * O servidor atribui o id. Signup/termos continuam no PUT legado.
 */
export async function createOrganizationPerson(
  row: BatchPersonValidRow,
  organizationId: string,
): Promise<void> {
  const resource: fhir5.Person = {
    resourceType: "Person",
    identifier: [
      {
        system: `urn:oid:${oids.cpf}`,
        value: row.cpf,
      },
    ],
    active: true,
    name: [{ text: row.name }],
    ...(row.birthDate ? { birthDate: row.birthDate } : {}),
    gender: toPersonGender(row.gender),
    telecom: [{ system: "phone", value: row.phone }],
    managingOrganization: {
      reference: buildPersonOrganizationReference(organizationId),
    },
    extension: [
      {
        url: TERM_REFUSED_URL,
        valueBoolean: false,
      },
    ],
  };
  await fhirClients.admin.create("Person", resource, {
    headers: {
      Accept: "application/fhir+json",
      "Content-Type": "application/fhir+json",
    },
  });
}

export const REGISTER_BATCH_SIZE = 5;

/** Cadastra em lotes de 5 com Promise.all. Continua os demais se um falhar. */
export async function registerPersonsInBatches(
  rows: BatchPersonValidRow[],
  organizationId: string,
  onProgress: (done: number, total: number) => void,
): Promise<BatchPersonFailure[]> {
  const failures: BatchPersonFailure[] = [];
  const total = rows.length;
  let done = 0;

  for (let index = 0; index < rows.length; index += REGISTER_BATCH_SIZE) {
    const batch = rows.slice(index, index + REGISTER_BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async (row) => {
        try {
          await createOrganizationPerson(row, organizationId);
          return null;
        } catch {
          console.error("Cadastro em lote: falha de rede");
          return { row } satisfies BatchPersonFailure;
        }
      }),
    );
    results.forEach((failure) => {
      if (failure) failures.push(failure);
    });
    done += batch.length;
    onProgress(done, total);
  }

  return failures;
}
